import {
  DEFAULT_SCENT,
  SCENTS,
  scentById,
  type ScentId,
} from "@roof-hawk/core";
import { useEffect, useRef, useState } from "react";

import { t } from "../../i18n";
import { applyScent, storedScent } from "../../lib/theme";

/**
 * A rep's own colours, picked from a short menu of scents.
 *
 * Sits beside the theme switch and works with it rather than instead of it:
 * the switch says light or dark, this says which colours. Only the rep sees
 * the result — a customer's proposal and contract stay in the company's red —
 * which is why the menu says so in its header.
 *
 * There used to be a "pick your own colours" card under the button until
 * somebody tried it. Jonathon turned it off on 2026-10-23 (ADR 0298); the
 * button alone is the way in now.
 */
export function ScentButton() {
  const [scent, setScent] = useState<ScentId>(storedScent);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    applyScent(scent);
  }, [scent]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(id: ScentId) {
    setScent(id);
    setOpen(false);
  }

  const current = scentById(scent);
  const label = t("Colours: {name}. Pick your own.", { name: t(current.name) });

  return (
    <div className="scentwrap" ref={wrap}>
      <button
        type="button"
        className={`themebtn scentbtn${scent === DEFAULT_SCENT ? "" : " on"}`}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="9.5" y="2.5" width="5" height="3.2" rx="0.8" />
          <path d="M10.5 5.7v3.1L6.6 13a6 6 0 1 0 10.8 0l-3.9-4.2V5.7" />
          <path d="M9.2 16.4c.9 1.1 2.1 1.6 3.4 1.4" />
        </svg>
      </button>

      {open && (
        <div
          className="scentmenu"
          role="menu"
          aria-label={t("Pick your colours")}
        >
          <div className="scentmenu-head">
            {t("My colours")} <span>{t("Only you see them")}</span>
          </div>
          {SCENTS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="menuitemradio"
              aria-checked={s.id === scent}
              className={`scentopt${s.id === scent ? " current" : ""}`}
              data-scent={s.id}
              onClick={() => pick(s.id)}
            >
              <span className="dot" aria-hidden="true" />
              <span className="scentopt-text">
                <span className="n">{t(s.name)}</span>
                <span className="d">{t(s.note)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
